import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AppBar, Toolbar, Typography, TextField, Grid, IconButton } from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import { Formik, Field, getIn } from 'formik';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';

// COMPONENTS
import Slide from '@/components/Transitions/Slide';

// STYLES
import {
  WriteReviewContainer,
  WriteReviewContent,
  HeaderToolbar,
  FooterAppBar,
  ButtonStyled,
  CloseIconStyled,
} from './styles';

const WriteReview = ({ name, logo, open, onClose, rating, onSubmit }) => {
  const [t] = useTranslation();
  const { isIframe, partner, user } = useSelector(({ app, partner: partnerStore, auth }) => ({
    isIframe: app.isIframe,
    partner: partnerStore.data,
    user: auth.user || {},
  }));

  const handleFormSubmit = values => {
    onSubmit(values);
    onClose();
  };

  return (
    <WriteReviewContainer
      fullScreen
      open={open}
      onClose={onClose}
      disableRestoreFocus
      TransitionComponent={Slide}
      className={clsx(
        'review-dialog',
        partner && `partner-${partner.partner_slug}`,
        isIframe && 'review-dialog--iframe',
      )}
    >
      <AppBar>
        <HeaderToolbar>
          {logo ? <img className="app-logo" src={logo} alt="logo" /> : <div />}
          <Typography variant="h6" align="center">
            {t('ratingsReviews.dialog.title')}
          </Typography>
          <IconButton size="medium" color="inherit" onClick={onClose} aria-label="close">
            <CloseIconStyled />
          </IconButton>
        </HeaderToolbar>
      </AppBar>
      <HeaderToolbar />
      <Formik
        initialValues={{
          rating,
          title: '',
          details: '',
          fullname: user.nickname || '',
          email: user.email || '',
        }}
        validationSchema={setYupValidationSchema('appDetailsWriteReview', t)}
        onSubmit={handleFormSubmit}
        // enableReinitialize
      >
        {({ isValid, handleSubmit }) => {
          return (
            <>
              <WriteReviewContent>
                <Typography className="m-b-15" variant="h3">
                  {t('ratingsReviews.dialog.everUsed', { name })}
                </Typography>
                <Field name="rating">
                  {({ field }) => {
                    return (
                      <Rating
                        {...field}
                        name={field.name}
                        value={+field.value}
                        className="sizeLarge write-review-content__rating"
                        precision={1}
                      />
                    );
                  }}
                </Field>

                <Grid container spacing={3} justifyContent="center">
                  <Grid item xs={12}>
                    <Field name="title">
                      {({ form, field }) => {
                        const error = getIn(form.errors, field.name);
                        const touch = getIn(form.touched, field.name);

                        return (
                          <TextField
                            {...field}
                            fullWidth
                            placeholder={t('inputs.title')}
                            error={touch && !!error}
                            helperText={touch && error}
                          />
                        );
                      }}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <Field name="details">
                      {({ form, field }) => {
                        const error = getIn(form.errors, field.name);
                        const touch = getIn(form.touched, field.name);

                        return (
                          <TextField
                            {...field}
                            multiline
                            minRows={9}
                            maxRows={9}
                            fullWidth
                            placeholder={t('inputs.details')}
                            error={touch && !!error}
                            helperText={touch && error}
                          />
                        );
                      }}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={3} justifyContent="center">
                      <Grid item xs={12} sm={6}>
                        <Field name="fullname">
                          {({ form, field }) => {
                            const error = getIn(form.errors, field.name);
                            const touch = getIn(form.touched, field.name);

                            return (
                              <TextField
                                {...field}
                                fullWidth
                                placeholder={t('inputs.fullName')}
                                error={touch && !!error}
                                helperText={touch && error}
                              />
                            );
                          }}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field name="email">
                          {({ form, field }) => {
                            const error = getIn(form.errors, field.name);
                            const touch = getIn(form.touched, field.name);

                            return (
                              <TextField
                                {...field}
                                fullWidth
                                placeholder={t('inputs.email')}
                                error={touch && !!error}
                                helperText={touch && error}
                              />
                            );
                          }}
                        </Field>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </WriteReviewContent>

              <FooterAppBar className="review-dialog__footer" component="div">
                <Toolbar>
                  <Grid container spacing={1} justifyContent="flex-end">
                    <Grid item>
                      <ButtonStyled size="medium" onClick={onClose}>
                        {t('buttons.cancel')}
                      </ButtonStyled>
                    </Grid>
                    <Grid item>
                      <ButtonStyled
                        variant="contained"
                        color="primary"
                        size="medium"
                        disabled={!isValid}
                        onClick={handleSubmit}
                      >
                        {t('buttons.submit')}
                      </ButtonStyled>
                    </Grid>
                  </Grid>
                </Toolbar>
              </FooterAppBar>
              {!isIframe && <Toolbar />}
            </>
          );
        }}
      </Formik>
    </WriteReviewContainer>
  );
};

WriteReview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  rating: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  logo: PropTypes.string,
  name: PropTypes.string.isRequired,
};

export default React.memo(WriteReview);
